<template>
    <b-row v-if="canSee">
        <b-col :cols="resolveContentCols" >
            <b-form-group
                id="identityFieldForGroup"
                :description="description"
                :label-cols-md="labelCols ? labelCols : 12"
            >
                
                <template #label>
                    {{ label ? label : field.name }} 

                    <template v-if="!canEdit || isProtected">
                        <b-badge class="fieldAlertTooltip" :id="`fieldAlertTooltip${field.key}`" variant="warning"><b-icon icon="exclamation-lg" scale="0.7" /></b-badge>
                        <b-tooltip :target="`fieldAlertTooltip${field.key}`">
                            <p style="font-size:12px" v-if="!canEdit">{{ $t('fieldRoleEditPermissions') }} </p>
                            <p style="font-size:12px" v-else-if="isProtected">{{ $t('fieldIsProtected') }} </p>
                        </b-tooltip>
                    </template>
                </template>

                <div>
                    <template v-if="field.provided_template">
                        <slot name="ProvidedTemplate"></slot>
                    </template>
 
                    <template v-else>
                        <div v-if="field.module == 'input'">
                            <TextField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'text'" v-bind="{...$attrs}" :extendErrors="extendErrors" :id="field.key" :translator="translator"/>
                            <EmailField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'email'" v-bind="{...$attrs}" :extendErrors="extendErrors" :id="field.key" :translator="translator"/>
                            <Texti18nField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'text_i18n'" v-bind="{...$attrs}" :extendErrors="extendErrors" :id="field.key" :store="store" :translator="translator"/>
                            <PasswordField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'password'" v-bind="{...$attrs}" :extendErrors="extendErrors" :page="page" :id="field.key" :translator="translator"/> 
                            <NumberField :field="field" :key="field.key" v-model.number="content" @error="error" v-if="field.type == 'number'"  :extendErrors="extendErrors" :id="field.key" :translator="translator" v-bind="{...$attrs}" />
                            <ImageV2Field :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'imagev2' || field.type == 'image'" v-bind="{...$attrs}" :id="field.key" :store="store" :translator="translator"/>
                            <FileField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'file'" v-bind="{...$attrs}" :id="field.key" :store="store" :translator="translator"/>
                        </div>

                        <div v-if="field.module == 'image'">
                            <ImageCarousel :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'carousel'" :id="field.key" v-bind="{...$attrs}" :store="store" :context="context" :translator="translator">
                                <template v-slot:IdentityFieldImageCarousel="{ itemSelected, mode }">
                                    <slot name="IdentityFieldImageCarousel" :itemSelected="itemSelected" :mode="mode"></slot>
                                </template>
                            </ImageCarousel>
                        </div>

                        <div v-if="field.module == 'text'">
                            <TextAreaField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'default'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                            <RichTextEditor :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'editor'" :id="field.key" v-bind="{...$attrs, }" :store="store" :context="context" :translator="translator"/>
                            <RichTexti18nEditor :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'editor_i18n'" :id="field.key" v-bind="{...$attrs, }" :store="store" :context="context" :translator="translator"/>
                            <DocumentEditor :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'document'" :id="field.key" v-bind="{...$attrs, }" :store="store" :context="context" :translator="translator"/>
                        </div>

                        <div v-if="field.module == 'select'">
                            <SelectTextField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'text'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                            <SelectNumberField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'number'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                            <SelectRelationField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'relation'" :id="field.key" v-bind="{...$attrs, }" :store="store" :context="context" :translator="translator">
                                <template v-slot:IdentityFieldSelectRelation>
                                    <slot name="IdentityFieldSelectRelation"></slot>
                                </template>
                            </SelectRelationField>
                            <SelectExternal :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'external'" :id="field.key" v-bind="{...$attrs, }" :store="$store" :context="context" :translator="translator"/>
                        </div>

                        <div v-if="field.module == 'picker'">
                            <DateField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'date'" :id="field.key" v-bind="{...$attrs, }" :store="store" :context="context" :translator="translator"/>
                            <DateRangeField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'daterange'" :id="field.key" v-bind="{...$attrs, }" :store="store" :context="context" :translator="translator"/>
                            <PickerTime :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'time'" :id="field.key" v-bind="{...$attrs, }" />
                            <PickerDateTime :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'datetime'" :id="field.key" v-bind="{...$attrs, }" :store="store" :context="context" :translator="translator"/>
                        </div>

                        <div v-if="field.module == 'checkbox'">
                            <CheckboxTextField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'text'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                            <CheckboxNumberField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'number'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                            <CheckboxBoolField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'bool'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                        </div>

                        <div v-if="field.module == 'radio'">
                            <RadioTextField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'text'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                            <RadioNumberField :field="field" :key="field.key" v-model="content" @error="error" v-if="field.type == 'number'" :id="field.key" v-bind="{...$attrs, }" :translator="translator"/>
                        </div>

                        <div v-if="field.module == 'table'">
                        <b-table :hover="true" :striped="false" :bordered="true" :small="false" :fixed="false" responsive="sm" :items="content" v-if="field.type == 'default'" :id="field.key" v-bind="{...$attrs, }"></b-table>
                            <TableCustom v-model="content" :key="field.key" :field="field" :id="field.key" v-if="field.type == 'custom'" v-bind="{...$attrs, }" :tranlator="translator"/>
                        </div>

                        <div v-if="field.module == 'fieldset'">
                            <DefaultFieldSet :field="field" :key="field.key" v-model="content" @error="error" v-bind="{...$attrs, }" :store="store" :context="context" v-if="field.type == 'default'" :translator="translator" />
                        </div>

                        <div v-if="field.module == 'data'">
                            <DataJson :field="field" :key="field.key" v-model="content" @error="error" v-bind="{...$attrs, }" :store="store" :context="context" v-if="field.type == 'json'" :translator="translator"  />
                        </div>

                        <div v-if="field.module == 'reference'">
                            <ReferenceText :field="field" :key="field.key" v-model="content" @error="error" v-bind="{...$attrs, }" :store="store" :context="context" v-if="field.type == 'text'" :translator="translator"  />
                        </div>
                    </template>
                </div>
            </b-form-group>
        </b-col>
    </b-row>
</template>

<style scoped>
.cover {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 999;
}

.fieldAlertTooltip {
    border-radius: 100px;
    padding: 2px;
    position: relative;
    top: -3px;
}
</style>

<script>
import Resolver from './tooltips/Resolver.vue'
import { Context } from '@protokol/platform-libs'
import Mention from './Mention.vue'
import CustomTemplate from './CustomTemplate'
import TextField from './fields/Text'
import EmailField from "./fields/Email.vue"
import Texti18nField from "./fields/Texti18n.vue"
import PasswordField from "./fields/Password.vue"
import ImageField from "./fields/Image.vue"
import ImageV2Field from "./fields/ImageV2.vue"
import FileField from "./fields/File.vue"
import TextAreaField from "./fields/TextArea.vue"
import NumberField from "./fields/Number.vue"
import DateField from "./fields/Date.vue"
import DateRangeField from "./fields/DateRange.vue"
import SelectTextField from "./fields/select/Text.vue"
import SelectNumberField from "./fields/select/Number.vue"
import SelectRelationField from "./fields/select/Relation.vue"
import CheckboxTextField from "./fields/checkbox/Text.vue"
import CheckboxBoolField from "./fields/checkbox/Bool.vue"
import SelectExternal from "./fields/select/External.vue"
import CheckboxNumberField from "./fields/checkbox/Number.vue"
import RadioTextField from "./fields/radio/Text.vue"
import RadioNumberField from "./fields/radio/Number.vue"
const RichTextEditor = () => import(/* webpackChunkName: "rich-text-editor" */ "./fields/RichTextEditor.vue")
const DocumentEditor = () => import(/* webpackChunkName: "document-editor" */ "./fields/DocumentEditor.vue")
const RichTexti18nEditor = () => import(/* webpackChunkName: "rich-text-i18n" */ "./fields/RichTextEditori18n.vue")
import TableCustom from "./fields/table/Custom/Index.vue"
import PickerTime from "./fields/Time.vue"
import PickerDateTime from "./fields/DateTime.vue"
import ImageCarousel from "./fields/image/Carousel.vue"
import DefaultFieldSet from "./fields/fieldset/Default.vue"
import DataJson from "./fields/data/Json.vue"
import ReferenceText from "./fields/reference/Text.vue"
import { defaultValueResolvers } from "./defsv2.js"
import { set, get } from 'lodash'

export default {
    name: 'IdentityField',
    components: {
        CustomTemplate,
        TextField,
        EmailField, 
        Texti18nField, 
        PasswordField,
        ImageField,
        ImageV2Field, 
        FileField,
        TextAreaField, 
        NumberField,
        DateField,
        DateRangeField,
        SelectTextField,
        SelectNumberField,
        SelectRelationField,
        CheckboxTextField,
        CheckboxBoolField,
        SelectExternal,
        CheckboxNumberField,
        RadioTextField,
        RadioNumberField,
        RichTextEditor,
        TableCustom,
        PickerTime,
        PickerDateTime,
        ImageCarousel,
        DefaultFieldSet,
        DataJson,
        ReferenceText,
        Resolver,
        Mention,
        RichTexti18nEditor,
        DocumentEditor
    },
    props: {
        field: {
            type: Object
        },
        value: {
            type: Object,
            default: function() {
                return {}
            }
        },
        labelCols: {
            type: Number,
            default: null
        },
        label: {
            type: String | Boolean,
            default: null
        },
        page: {
            type: String
        },
        extendErrors: {
            type: Array,
            default: function() {
                return []
            }
        },
        helpTip: {
            type: Object,
            default: null
        },
        store: {
            type: Object,
            default: null
        },
        context: {
            type: Object,
            default: function() {
                return {}
            }
        }
    },
    data: function() {
        return {
            helpfulTipModal: {
                show: false
            },
            mutated: 0,
            compVersion: 0,
            mentionInterface: false
        }
    },

    methods: {
        error(errors) {
            this.$emit('error', errors)
        },

        decideToSetDefaultValue() {
            const constr = this.field.constraints || {}

            
            if (constr.default && !this.content) {
                const r = defaultValueResolvers[`${this.field.module}:${this.field.type}`]
                if (r) {
                    let p = r(this.field.constraints.default)

                    this.$emit('input', { [this.field.key]: p })
                } else {
                    this.$emit('input', { [this.field.key]: constr.default })
                }
            }
        }
    }, 
    computed: {
        content: {
            get() {
                const value = this.value || {}

                return get(value, this.field.key)
            },

            set: function(val) {
                if (!this.canEdit) {
                    let i = {}
                    set(i, this.field.key, this.content)
                    return this.$emit('input', i)
                }

                // descruct object and output as independent values
                if (val !== undefined && val !== null) {
                    if (val._destruct) {
                        delete val['_destruct']
                        // Convert any dot-notation keys to properly nested objects
                        const nested = {}
                        Object.keys(val).forEach(function(key) {
                            set(nested, key, val[key])
                        })
                        this.$emit('input', nested)
                    } else {
                        let i = {}
                        set(i, this.field.key, val)
                        this.$emit('input', i)
                    }
                } else {
                    let i = {}
                    set(i, this.field.key, null)
                    this.$emit('input', i)
                }
            }
        },

        globalContext() {
            // get values that are instructed by field context command var:{name}
            let vars = {}

            if (this.field.context) {
                this.field.context.forEach(c => {
                    // check command
                    let command = c.key.split(":");
                    if (command[0] == "var") {
                        vars[command[1]] = c.value
                    }
                })
            }

            return new Context({
                vars: vars,
                ...this.context || {},
                locale: this.$store.getters.getLocale
            })
        },

        resolveContentCols() {
            return 12 
        },

        translator() {
            if (this.$attrs.translator) {
                return this.$attrs.translator
            }
            
            return this.$t
        },

        description() {
            return this.$translateLabel(this.field.description) || this.field.description
        },

        canSee() {
            if (!this.store) return true
            if (this.store.getters.isAdmin) return true
            if (this.field.roles.see === null) return true
            if (this.field.roles.see.length < 1) return true

            return this.store.getters.getRoles.find(r => {
                return this.field.roles.see.find(seeR => {
                    return seeR == r.uuid
                })
            })
        },

        canEdit() {
            if (this.isProtected) {
                return false
            }

            const { getters } = this.store || {}
            if (!getters) return true
            if (getters.isAdmin) return true

            if (this.field.roles.edit === null) return true
            if (this.field.roles.edit.length < 1) return true

            return getters.getRoles.find(r => {
                return this.field.roles.edit.find(seeR => {
                    return seeR == r.uuid
                })
            }) !== undefined
        },

        isProtected() {
            return this.field.protected && this.page == 'model_edit'
        }
    },

    async mounted() {
        this.decideToSetDefaultValue()
    }
}
</script>